import React, { useCallback, useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FlashList } from '@shopify/flash-list';
import { ConversationBubble } from './ConversationBubble';
import { BottomTextInput, IBottomTextInputRef } from '@/components/ui/BottomTextInput';
import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { MessagesService } from '~/services/domain/MessagesService';
import { PhotosService } from '~/services/domain/PhotosService';
import { ConversationView, ConversationItem } from '@/models/Conversation';
import type { MessagesView } from '@/models/Message';
import { Photo } from '@/models/Photo';
import {
  buildInboxPhotoSentinel,
  buildInboxSentinel,
  buildMarkup,
  type IMediaItem,
  IMediaShareContext,
  type InboxSentinelRole,
} from '@/models/Media';
import { MessageView } from '@/components/ui/MessageView';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { Alert02Icon, LicenseNoIcon } from '@hugeicons-pro/core-stroke-rounded';
import { useTranslation } from 'react-i18next';
import { useIsFocused } from 'expo-router/react-navigation';
import { VStack } from '../../gluestack/vstack';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useSignalR, useSignalRUpdates } from '@/components/providers/SignalRProvider';
import { Center } from '@/components/gluestack/center';
import { Spinner } from '@/components/gluestack/spinner';
import { ProfilePicture } from '@/components/domain/common/ProfilePicture';
import { Box } from '~/components/gluestack/box';
import {
  KeyboardAvoidingView,
  useReanimatedKeyboardAnimation,
} from 'react-native-keyboard-controller';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { useSupportService } from '@/hooks/useSupportService';
import { useCommunity } from '@/hooks/useCommunity';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { PremiumUpgradeCard } from '@/components/domain/common/PremiumUpgradeCard';
import { NewAccountNotice } from '@/components/domain/messages/NewAccountNotice';
import { useTimestampVisibility } from '@/hooks/useTimestampVisibility';
import { usePhotoUpload } from '@/components/ui/image/usePhotoUpload';
import { useQueryCacheUtils } from '@/hooks/useQueryCacheUtils';
import { Dimensions, Platform } from 'react-native';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { ReportReasonSheet } from '@/components/domain/common/ReportReasonSheet';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppState } from '@/hooks/useAppState';
import { useKeyboardVisible } from '@/hooks/useKeyboard';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';
import { useDismissKeyboardOnScroll } from '@/hooks/useDismissKeyboardOnScroll';
import { ensureFlashListScrollToEnd } from '@/hooks/ensureFlashListScrollToEnd';
import { mediaRegistry } from '@/services/media/registry';
import {
  getKlipyGifQueryOptions,
  warmKlipyGifMetadata,
} from '@/components/ui/media/providers/klipy/klipyGifQuery';
import {
  getKlipyGifAspectRatio,
  getKlipyGifPreview,
} from '@/components/ui/media/providers/klipy/klipyGifUtils';
import type { IKlipyGif } from '@/services/media/providers/klipy/types';
import { useKlipyApiKey } from '@/hooks/useKlipyApiKey';
import { WebDropImageUpload } from '@/components/ui/WebDropImageUpload';
import { useAuth } from '@/components/providers/AuthProvider';
import { useAccountSettings } from '@/hooks/useAccountSettings';
import type { Member } from '@/models/Member';

interface ConversationScreenProps {
  messageId?: number;
  disableScreenHeader?: boolean;
}

/**
 * Builds a lightweight Photo for the image-viewer carousel from a conversation
 * item's attachment. Conversation photos are not Photo records (no id, likes, or
 * comments), so the viewer is opened with `disableSocialActions` and matches the
 * tapped photo by its full-size URI. The sender (inferred from the conversation)
 * and the message date are attached so the viewer header can show basic info.
 *
 * `IsOwner` drives the viewer's per-photo download affordance: only our own
 * photos are downloadable (privacy). `canDownloadOwn` lets the caller suppress
 * it on iOS tablet, where saving from the viewer fights ConversationScreen's
 * FlashList + KeyboardAvoidingView and jumps the thread to the top.
 */
function buildConversationPhoto(
  item: ConversationItem,
  sender?: Member,
  canDownloadOwn = false
): Photo {
  const photo = new Photo();
  photo.FullSizeUrl = item.PhotoFullSizeUrl ?? '';
  photo.IconUrl = item.PhotoThumbnailUrl ?? item.PhotoFullSizeUrl ?? '';
  photo.DatePosted = item.DateCreated;
  if (sender) photo.Member = sender;
  photo.IsOwner = item.IsFromMe && canDownloadOwn;
  return photo;
}

export function ConversationScreen({
  messageId,
  disableScreenHeader = false,
}: ConversationScreenProps) {
  const { t } = useTranslation();
  const { data: accountSettings } = useAccountSettings();
  const isMessageGifPickerEnabled = accountSettings?.MediaSendDisabled !== true;
  const isScreenActive = useIsFocused();
  const { showErrorToast } = useToastMessages();
  const { setMessageId, isConnected: isSignalRConnected } = useSignalR();
  const { messageId: signalRMessageId } = useSignalRUpdates();
  const listRef = useRef<any>(null);
  /** Scroll-to-end only via `onContentSizeChange` while this timestamp is in the future (initial layout / remeasure). */
  const pinListToEndUntilRef = useRef(0);
  const bottomInputRef = useRef<IBottomTextInputRef>(null);
  /** Measured list-pane width (VStack onLayout) — feeds text height estimates. */
  const listWidthRef = useRef(0);
  const { updateCacheItem } = useQueryCacheUtils();
  const { submitTicket, submitPolicyViolationForConversation } = useSupportService();
  const [showReportReasonSheet, setShowReportReasonSheet] = useState(false);
  const community = useCommunity();
  const queryClient = useQueryClient();

  // Opened from the conversation list: its cached row already has the member, so the header needs no loading state
  const cachedListMember = useMemo(() => {
    if (!messageId) return undefined;
    for (const [, cached] of queryClient.getQueriesData<InfiniteData<MessagesView>>({
      queryKey: ['messages'],
    })) {
      const match = cached?.pages
        ?.flatMap((page) => page?.MessageItems ?? [])
        .find((message) => message?.Id === messageId);
      if (match?.Member) return match.Member;
    }
    return undefined;
  }, [messageId, queryClient]);
  const klipyApiKey = useKlipyApiKey();

  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const isLargeScreen = useIsLargeScreen();
  // History prefetch fires ~2.5 screens before the top (1.5 on large screens) —
  // at the default ~1 screen the page-prepend settle could land in-viewport and
  // show as brief tearing on a cold first scroll-up (iOS, QA 2026-07-10); the
  // earlier trigger lets the prepend and its offset correction settle offscreen.
  // Side effect via the optimizer: conversation pages grow 30 → 48 items.
  const { onEndReachedThreshold: onStartReachedThreshold, pageSize } = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 12, desktop: 16 },
    thresholdMobile: 2.5,
    thresholdDesktop: 1.5,
  });
  const insets = useSafeAreaInsets();
  const isKeyboardVisible = useKeyboardVisible();

  // Tablet master-detail right pane (MessagesScreen). The KAV's single-shot
  // automaticOffset measurement goes stale on this pane's remount/rotation path
  // (composer ends up behind the keyboard), so both platforms lift the composer
  // with the global animated keyboard height via the spacer below the input.
  const isTabletSplitDetail =
    disableScreenHeader && (Platform.OS === 'ios' || Platform.OS === 'android');
  const { height: animatedKeyboardHeight } = useReanimatedKeyboardAnimation();
  const splitComposerInsetStyle = useAnimatedStyle(
    () => ({ height: Math.max(Math.abs(animatedKeyboardHeight.value), insets.bottom) }),
    [insets.bottom]
  );
  const { isUploading: isImageUploading, uploadPhoto: uploadImage } = usePhotoUpload();
  const [restoredDraftText, setRestoredDraftText] = useState('');
  const draftTextRef = useRef('');
  const draftPersistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { accountStatus } = useAuth();
  const dismissKeyboardOnScroll = useDismissKeyboardOnScroll();

  /**
   * Helper function to update message cache based on the latest conversation item
   * from a fetch result. Handles both text and photo messages, and sets the read status.
   */
  const updateMessageCacheWithLatestConversationItem = useCallback(
    (conversationId: number, fetchResult: any) => {
      const latestPage = fetchResult.data?.pages?.[fetchResult.data.pages.length - 1];
      if (latestPage?.ConversationItems && latestPage.ConversationItems.length > 0) {
        const latestMessage = latestPage.ConversationItems[0]; // Newest message is first
        const rootMessage = fetchResult.data?.pages?.[0]?.Message;
        const isOriginalSender = rootMessage?.IsOriginalSender ?? false;
        const role: InboxSentinelRole = latestMessage.IsFromMe
          ? isOriginalSender
            ? 'S'
            : 'R'
          : isOriginalSender
            ? 'R'
            : 'S';

        let lastText = latestMessage.Text;
        if (!lastText && latestMessage.PhotoThumbnailUrl) {
          lastText = buildInboxPhotoSentinel(role);
        } else if (!lastText && latestMessage.Media) {
          lastText = buildInboxSentinel(latestMessage.Media.Type, role);
        }

        updateCacheItem(
          'messages',
          conversationId,
          {
            LastText: lastText || '',
            LastUpdate:
              typeof latestMessage.DateCreated === 'string'
                ? latestMessage.DateCreated
                : latestMessage.DateCreated.toISOString(),
            IsRead: true,
            IsLastFromMe: latestMessage.IsFromMe,
          },
          'MessageItems'
        );
      }
    },
    [updateCacheItem]
  );

  // Create a unique query key for this conversation
  const conversationQueryKey = ['conversation', messageId];

  // Build a per-conversation storage key
  const draftStorageKey = messageId ? `draft.conversation.${messageId}` : undefined;

  const writeDraftText = useCallback(
    (text: string) => {
      if (!draftStorageKey) return;

      const trimmed = text.trim();
      if (trimmed.length > 0) {
        void AsyncStorage.setItem(draftStorageKey, text);
      } else {
        void AsyncStorage.removeItem(draftStorageKey);
      }
    },
    [draftStorageKey]
  );

  const handleDraftTextChange = useCallback(
    (text: string) => {
      draftTextRef.current = text;

      if (draftPersistTimeoutRef.current) {
        clearTimeout(draftPersistTimeoutRef.current);
      }

      draftPersistTimeoutRef.current = setTimeout(() => {
        draftPersistTimeoutRef.current = null;
        writeDraftText(text);
      }, 300);
    },
    [writeDraftText]
  );

  const { data, error, fetchNextPage, fetchPreviousPage, isPending, refetch } = useInfiniteQuery({
    queryKey: conversationQueryKey,
    queryFn: async ({ pageParam }) => {
      if (!messageId) {
        throw new Error('Missing message id');
      }

      const params: any = { take: pageSize };

      // Handle bidirectional pagination
      if (pageParam?.direction === 'next' && pageParam?.skip) {
        params.nextPageSkip = pageParam.skip;
      } else if (pageParam?.direction === 'previous' && pageParam?.skip) {
        params.previousPageSkip = pageParam.skip;
      }

      const result = await MessagesService.getConversation(messageId, params);

      // A page only commits once its GIFs' metadata is cached (2s cap), so
      // bubbles always mount at final size — the first layout is stable and
      // history prepends can't shift the scroll position.
      await warmKlipyGifMetadata(
        queryClient,
        klipyApiKey,
        (result.ConversationItems ?? []).map((item) => item.Media)
      );

      return result;
    },
    enabled: !!messageId,
    initialPageParam: { direction: 'initial', skip: undefined },
    getNextPageParam: (lastPage) =>
      lastPage.NextPageSkip ? { direction: 'next', skip: lastPage.NextPageSkip } : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage.PreviousPageSkip
        ? { direction: 'previous', skip: firstPage.PreviousPageSkip }
        : undefined,
    select: useCallback((data: InfiniteData<ConversationView, any>) => {
      // Flatten conversation items from all pages
      const items = data.pages.flatMap((page) => page.ConversationItems);

      // Get the root message info from the first page
      const rootMessage = data.pages[0]?.Message;

      // Check if the oldest page has hit the non-premium history limit
      const oldestPage = data.pages[0];
      const nonPremiumLimitReached =
        oldestPage?.NonPremiumLimitReached === true && !oldestPage?.PreviousPageSkip;

      // Only once the start of the conversation is loaded, so it doesn't flash in above older pages
      const showNewAccountNotice =
        rootMessage?.IsOtherMemberNew === true && !oldestPage?.PreviousPageSkip;

      return {
        ...data,
        items,
        rootMessage,
        totalCount: data.pages[0]?.ItemCount,
        nonPremiumLimitReached,
        showNewAccountNotice,
      };
    }, []),
    staleTime: 1000 * 60,
    gcTime: 0,
  });

  const timestampVisibility = useTimestampVisibility(data?.items || []);
  const selfMember = useMemo(
    () =>
      accountStatus
        ? ({
            Id: accountStatus.MemberId,
            UserName: accountStatus.UserName,
            IconUrl: accountStatus.IconUrl,
          } as Member)
        : undefined,
    [accountStatus?.MemberId, accountStatus?.UserName, accountStatus?.IconUrl]
  );
  const otherMember = useMemo(
    () =>
      data?.rootMessage?.Member
        ? ({
            Id: data.rootMessage.Member.Id,
            UserName: data.rootMessage.Member.UserName,
            IconUrl: data.rootMessage.Member.IconUrl,
            IsLocked: data.rootMessage.Member.IsLocked,
            IsEvent: data.rootMessage.Member.IsEvent,
          } as Member)
        : undefined,
    [
      data?.rootMessage?.Member?.Id,
      data?.rootMessage?.Member?.UserName,
      data?.rootMessage?.Member?.IconUrl,
      data?.rootMessage?.Member?.IsLocked,
      data?.rootMessage?.Member?.IsEvent,
    ]
  );

  const isAdminConversation = (() => {
    const userName = data?.rootMessage?.Member?.UserName?.toLowerCase();
    const communityName = community?.name?.toLowerCase();
    if (!userName || !communityName) return false;
    return userName === communityName;
  })();

  // Rendered with the first message rather than as the list header, which
  // startRenderingFromBottom leaves at the top of a short conversation
  const newAccountNoticeItemId =
    data?.showNewAccountNotice && !isAdminConversation ? data.items[0]?.Id : undefined;
  const otherUserName = data?.rootMessage?.Member?.UserName ?? '';

  // Carousel of photos shared in this conversation, built only from the items
  // already loaded into the list (front-end only, so it respects the same
  // paywall history limit as the thread). Held in a single ref so every bubble
  // receives the same stable `getPhotos` callback — no new array prop per
  // pagination or cache update. The viewer matches the tapped photo by URI.
  // iOS tablet excluded — saving from the viewer jumps the thread to the top
  // (FlashList + KeyboardAvoidingView relayout). Re-confirmed 2026-06-15 that it
  // still reproduces with the SDK 56 Asset.create flow, so the 6.0.181 guard
  // remains necessary. See buildConversationPhoto.
  const canDownloadOwnPhotos = !(Platform.OS === 'ios' && isLargeScreen);
  const conversationPhotos = useMemo(
    () =>
      (data?.items ?? [])
        .filter((item) => item.PhotoFullSizeUrl)
        .map((item) =>
          buildConversationPhoto(
            item,
            item.IsFromMe ? selfMember : otherMember,
            canDownloadOwnPhotos
          )
        ),
    [data?.items, selfMember, otherMember, canDownloadOwnPhotos]
  );
  const conversationPhotosRef = useRef<Photo[]>([]);
  conversationPhotosRef.current = conversationPhotos;
  const getConversationPhotos = useCallback(() => conversationPhotosRef.current, []);

  /**
   * Display height of a GIF bubble's image, read from the metadata warmed
   * before its page committed — mirrors KlipyGifView's sizing (md preview,
   * 240 default max width). Undefined while metadata is still pending.
   */
  const getGifDisplayHeight = useCallback(
    (media: IMediaItem) => {
      const gif = queryClient.getQueryData<IKlipyGif | null>(
        getKlipyGifQueryOptions(klipyApiKey, media.Id ?? '').queryKey
      );
      if (!gif) return undefined;
      const width = Math.min(getKlipyGifPreview(gif, 'md')?.width || 240, 240);
      return width / getKlipyGifAspectRatio(gif, 'md');
    },
    [queryClient, klipyApiKey]
  );

  /** Height class for a GIF bubble (~48px steps); shared bucket while metadata is pending. */
  const getMediaHeightBucket = useCallback(
    (media: IMediaItem) => {
      const height = getGifDisplayHeight(media);
      return height === undefined ? 'x' : Math.round(height / 48);
    },
    [getGifDisplayHeight]
  );

  /**
   * Deterministic height estimate for a bubble that has never been measured,
   * fed to FlashList through overrideItemLayout — honored (via our flash-list
   * patch) only while an item's type bucket has no measurements, replacing the
   * library's fixed 200px cold default that measured as ±150-250px scroll
   * jumps. Once a bucket has real samples its rolling average wins. Constants
   * calibrated on-device 2026-07-10: 1-line text bubble = 43px (16px vertical
   * padding/margins + 27px line), timestamp row = +30px. The new-account
   * notice's +120px is a rough, uncalibrated estimate.
   */
  const estimateConversationItemHeight = useCallback(
    (item: ConversationItem) => {
      const headerRowsHeight =
        ((timestampVisibility[item.Id] ?? false) || item.Id === -1 ? 30 : 0) +
        (item.Id === newAccountNoticeItemId ? 120 : 0);
      if (item.PhotoThumbnailUrl) {
        return 200 + headerRowsHeight; // 192px thumbnail + margins
      }
      if (item.Media) {
        const gifHeight = getGifDisplayHeight(item.Media);
        return (gifHeight !== undefined ? gifHeight + 8 : 216) + headerRowsHeight;
      }
      const listWidth = listWidthRef.current || Dimensions.get('window').width;
      // mx-4 (32) + avatar column (36) → bubble max-w-[75%] → px-4 (32)
      const textWidth = Math.max(80, (listWidth - 68) * 0.75 - 32);
      const charsPerLine = textWidth / 7.4;
      const text = (item.Text ?? '').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '');
      const lines = text
        .split('\n')
        .reduce(
          (total, segment) => total + Math.max(1, Math.ceil(segment.length / charsPerLine)),
          0
        );
      return lines * 27 + 16 + headerRowsHeight;
    },
    [timestampVisibility, getGifDisplayHeight, newAccountNoticeItemId]
  );

  const overrideConversationItemLayout = useCallback(
    (layout: { span?: number; size?: number }, item: ConversationItem) => {
      layout.size = estimateConversationItemHeight(item);
    },
    [estimateConversationItemHeight]
  );

  /**
   * FlashList v2 sizes every not-yet-measured item with its type bucket's
   * rolling average height and applies each miss as a scroll-offset correction,
   * so scrolling back through unmeasured history jumps when a bucket mixes very
   * different heights (a long text bubble estimated as a one-liner). Besides
   * recycling by bubble shape (me/other × photo/media/text, mirroring
   * ConversationBubble's content branches), bucket text by length class and
   * GIFs by display height so estimates stay within ~a text line of reality;
   * items showing the ~30px timestamp row get their own buckets.
   */
  const getConversationItemType = useCallback(
    (item: ConversationItem) => {
      const base = item.IsFromMe ? 'me' : 'other';
      const timestamp =
        ((timestampVisibility[item.Id] ?? false) || item.Id === -1 ? '-ts' : '') +
        (item.Id === newAccountNoticeItemId ? '-notice' : '');
      if (item.PhotoThumbnailUrl) {
        return `${base}-photo${timestamp}`;
      }
      if (item.Media) {
        return `${base}-media-${getMediaHeightBucket(item.Media)}${timestamp}`;
      }
      // Geometric length classes ≈ line-count classes (~1, 2, 4, 8… lines)
      const textLength = item.Text?.length ?? 0;
      let bucket = 0;
      for (let bound = 24; textLength > bound && bucket < 6; bound *= 2) {
        bucket++;
      }
      return `${base}-text-${bucket}${timestamp}`;
    },
    [timestampVisibility, getMediaHeightBucket, newAccountNoticeItemId]
  );

  const renderConversationItem = useCallback(
    ({ item }: { item: ConversationItem }) => (
      <>
        {item.Id === newAccountNoticeItemId && <NewAccountNotice userName={otherUserName} />}
        <ConversationBubble
          item={item}
          isTimestampVisible={(timestampVisibility[item.Id] ?? false) || item.Id === -1}
          selfMember={selfMember}
          otherMember={otherMember}
          getPhotos={getConversationPhotos}
        />
      </>
    ),
    [
      timestampVisibility,
      selfMember,
      otherMember,
      getConversationPhotos,
      newAccountNoticeItemId,
      otherUserName,
    ]
  );

  // Restore draft on conversation change
  useEffect(() => {
    let isMounted = true;
    draftTextRef.current = '';
    setRestoredDraftText('');

    if (!draftStorageKey) {
      return () => {
        isMounted = false;
        if (draftPersistTimeoutRef.current) {
          clearTimeout(draftPersistTimeoutRef.current);
          draftPersistTimeoutRef.current = null;
        }
      };
    }

    (async () => {
      try {
        const stored = await AsyncStorage.getItem(draftStorageKey);
        if (isMounted) {
          const text = stored || '';
          draftTextRef.current = text;
          setRestoredDraftText(text);
        }
      } catch {}
    })();

    return () => {
      isMounted = false;
      if (draftPersistTimeoutRef.current) {
        clearTimeout(draftPersistTimeoutRef.current);
        draftPersistTimeoutRef.current = null;
        writeDraftText(draftTextRef.current);
      }
    };
  }, [draftStorageKey, writeDraftText]);

  // Mark as read when opening a conversation
  useEffect(() => {
    if (messageId && !isPending) {
      updateCacheItem('messages', messageId, { IsRead: true }, 'MessageItems');
    }
  }, [messageId, isPending, updateCacheItem]);

  useLayoutEffect(() => {
    if (!messageId || isPending || error || !data?.items?.length) {
      pinListToEndUntilRef.current = 0;
      return;
    }
    pinListToEndUntilRef.current = Date.now() + 600;
    // Only when loading finishes for this thread — not when `items.length` changes from prepending history
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, isPending, error]);

  const handleConversationContentSizeChange = useCallback(() => {
    if (Date.now() > pinListToEndUntilRef.current) return;
    listRef.current?.scrollToEnd?.({ animated: false });
  }, []);

  /** Pull messages newer than what is loaded and sync the inbox cache entry. */
  const refreshLatestMessages = useCallback(async () => {
    if (!messageId) return;

    const result = await fetchNextPage();
    updateMessageCacheWithLatestConversationItem(messageId, result);
  }, [messageId, fetchNextPage, updateMessageCacheWithLatestConversationItem]);

  // App foreground and SignalR reconnect fire back-to-back when reopening the
  // app; the timestamp guard collapses them into a single fetch.
  const lastRecoveryRefreshAtRef = useRef(0);
  const recoverMissedMessages = useCallback(() => {
    const now = Date.now();
    if (now - lastRecoveryRefreshAtRef.current < 2500) return;
    lastRecoveryRefreshAtRef.current = now;
    void refreshLatestMessages();
  }, [refreshLatestMessages]);

  // Replies that arrive while updates can't be received (app backgrounded,
  // SignalR down) are never re-signaled by the hub, so refresh whenever a
  // window where events could have been missed closes.
  useAppState({
    onForeground: useCallback(() => {
      if (isScreenActive) {
        recoverMissedMessages();
      }
    }, [isScreenActive, recoverMissedMessages]),
  });

  const prevIsSignalRConnectedRef = useRef(isSignalRConnected);
  useEffect(() => {
    const wasConnected = prevIsSignalRConnectedRef.current;
    prevIsSignalRConnectedRef.current = isSignalRConnected;

    if (!wasConnected && isSignalRConnected && isScreenActive) {
      recoverMissedMessages();
    }
  }, [isSignalRConnected, isScreenActive, recoverMissedMessages]);

  // Handle SignalR updates for this specific conversation
  useEffect(() => {
    if (signalRMessageId === messageId && isScreenActive) {
      const handleSignalRUpdate = async () => {
        await refreshLatestMessages();

        // Reset the signalR messageId so we don't re-trigger
        setMessageId(null);
      };

      handleSignalRUpdate();
    }
  }, [signalRMessageId, messageId, isScreenActive, setMessageId, refreshLatestMessages]);

  const handleImageUpload = async (imageUri: string | File) => {
    if (!messageId) return;

    await uploadImage(imageUri, async (imageUri: string | File) => {
      await PhotosService.uploadPhotoToMessage(imageUri, messageId);
      const result = await fetchNextPage();

      // Update the message list cache with the new photo message
      updateMessageCacheWithLatestConversationItem(messageId, result);

      ensureFlashListScrollToEnd(listRef);
    });
  };

  const handleSubmitReply = async (text: string) => {
    if (!text.trim() || !messageId) return;

    setIsSubmittingReply(true);
    try {
      await MessagesService.addReply(messageId, { Text: text.trim() });

      const result = await fetchNextPage();

      // Update the message list cache with the new reply
      updateMessageCacheWithLatestConversationItem(messageId, result);

      ensureFlashListScrollToEnd(listRef);
      // Clear draft after successful send
      if (draftPersistTimeoutRef.current) {
        clearTimeout(draftPersistTimeoutRef.current);
        draftPersistTimeoutRef.current = null;
      }
      draftTextRef.current = '';
      if (draftStorageKey) {
        await AsyncStorage.removeItem(draftStorageKey);
      }
      setRestoredDraftText('');
    } catch (error) {
      showErrorToast(t('messages.replyFailed'), error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleMediaSelected = async (item: IMediaItem, ctx?: IMediaShareContext) => {
    if (!messageId || !item.Id) return;

    setIsSubmittingReply(true);
    try {
      await MessagesService.addReply(messageId, {
        Text: buildMarkup(item.Type, item.Provider, item.Id),
      });

      void mediaRegistry.get(item.Provider)?.triggerShare(item, ctx);

      const result = await fetchNextPage();
      updateMessageCacheWithLatestConversationItem(messageId, result);
      ensureFlashListScrollToEnd(listRef);

      if (draftPersistTimeoutRef.current) {
        clearTimeout(draftPersistTimeoutRef.current);
        draftPersistTimeoutRef.current = null;
      }
      draftTextRef.current = '';
      if (draftStorageKey) {
        await AsyncStorage.removeItem(draftStorageKey);
      }
      setRestoredDraftText('');
    } catch (error) {
      showErrorToast(t('messages.replyFailed'), error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const headerMember = data?.rootMessage?.Member ?? cachedListMember;

  // If no messageId is provided, show empty state after hooks have been registered.
  if (!messageId) {
    return <MessageView type="info" text="" icon={LicenseNoIcon} iconSize={64} />;
  }

  const conversationPaddingBottom = isTabletSplitDetail
    ? 0 // handled by the animated keyboard spacer below the composer
    : isLargeScreen && !isKeyboardVisible
      ? insets.bottom
      : 0;

  return (
    <KeyboardAvoidingView
      behavior={'position'}
      enabled={!isTabletSplitDetail}
      automaticOffset={Platform.OS === 'ios' && !isTabletSplitDetail}
      contentContainerStyle={{ flex: 1 }}
      style={{ flex: 1, overflow: 'hidden' }}>
      <VStack
        className="flex-1 rounded-tl-xl bg-background-0"
        style={{ paddingBottom: conversationPaddingBottom }}
        onLayout={(event) => {
          listWidthRef.current = event.nativeEvent.layout.width;
        }}>
        {/* Header */}
        {!disableScreenHeader && (
          <ScreenHeader
            options={{
              title: headerMember?.UserName || t('messages.conversation'),
              // Phones: photo and name on the left (a placeholder while loading), the report button on the right.
              // A conversation that failed to load keeps the plain centered title.
              ...(!isLargeScreen &&
                (headerMember || isPending) && {
                  hideTitle: true,
                  headerLeft: () =>
                    headerMember ? (
                      <HStack
                        className="items-center gap-2"
                        style={{ maxWidth: Dimensions.get('window').width - 140 }}>
                        <ProfilePicture member={headerMember} size="sm" />
                        <Text size="lg" bold className="shrink text-white" numberOfLines={1}>
                          {headerMember.UserName}
                        </Text>
                      </HStack>
                    ) : (
                      <HStack className="items-center gap-2">
                        <Box className="h-10 w-10 rounded-full bg-white/20" />
                        <Box className="h-4 w-28 rounded-md bg-white/20" />
                      </HStack>
                    ),
                }),
              ...(data?.rootMessage?.Member && {
                headerRight: () => (
                  <HStack className="items-center gap-3">
                    {!isAdminConversation && (
                      <ButtonWrapper
                        icon={Alert02Icon}
                        iconSize={20}
                        size="sm"
                        inAppHeaderBar={!isLargeScreen}
                        onPress={() => setShowReportReasonSheet(true)}
                        accessibilityLabel={t('messages.menu.reportConversation')}
                      />
                    )}
                    {isLargeScreen && (
                      <ProfilePicture member={data.rootMessage!.Member} size="sm" />
                    )}
                  </HStack>
                ),
              }),
            }}
          />
        )}
        <ReportReasonSheet
          isOpen={showReportReasonSheet}
          onClose={() => setShowReportReasonSheet(false)}
          subject="conversation"
          onReasonSelected={(reason) => submitPolicyViolationForConversation(messageId, reason)}
        />

        {/* Conversation Content */}
        {error ? (
          <Center className="flex-1">
            <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={false} />
          </Center>
        ) : isPending ? (
          <Center className="flex-1">
            <Spinner size="large" color="grey" />
          </Center>
        ) : !data?.items.length ? (
          <Center className="flex-1">
            <MessageView
              type="warning"
              text={t('messages.emptyConversation')}
              icon={LicenseNoIcon}
            />
          </Center>
        ) : (
          <FlashList
            ref={listRef}
            data={data.items}
            renderItem={renderConversationItem}
            keyExtractor={(item) => `${item.Id}`}
            getItemType={getConversationItemType}
            overrideItemLayout={overrideConversationItemLayout}
            style={{ flex: 1, backgroundColor: 'transparent' }}
            contentContainerStyle={{ paddingVertical: 8 }}
            ListHeaderComponent={
              data.nonPremiumLimitReached ? (
                <Box className="px-4 pb-2">
                  <PremiumUpgradeCard listType="conversation" />
                </Box>
              ) : undefined
            }
            maintainVisibleContentPosition={{
              startRenderingFromBottom: true,
              autoscrollToBottomThreshold: 0.2,
            }}
            onStartReached={data.nonPremiumLimitReached ? undefined : fetchPreviousPage}
            onStartReachedThreshold={onStartReachedThreshold}
            onContentSizeChange={handleConversationContentSizeChange}
            {...dismissKeyboardOnScroll}
          />
        )}

        {/* Bottom Input or Admin Notice */}
        <Box className="bg-background-0 px-4 pb-4">
          {isAdminConversation ? (
            <Center className="rounded-xl bg-primary-200">
              <MessageView
                type="info"
                text={t(
                  'messages.adminClosedInfo',
                  'This conversation is closed and replies are disabled...'
                )}
                disableHeight={true}>
                <Center className="mt-2">
                  <ButtonWrapper
                    text={t('messages.contactSupport', 'Contact Support')}
                    size="md"
                    onPress={() => submitTicket('Conversation follow-up')}
                  />
                </Center>
              </MessageView>
            </Center>
          ) : (
            <BottomTextInput
              key={messageId}
              ref={bottomInputRef}
              onSubmit={handleSubmitReply}
              onMediaSelected={handleMediaSelected}
              gifPickerMode={isMessageGifPickerEnabled ? 'enabled' : 'none'}
              isSubmitting={isSubmittingReply}
              placeholder={t('messages.typeMessage')}
              imageUploadMode="preview"
              onImageUpload={handleImageUpload}
              isImageUploading={isImageUploading}
              initialText={restoredDraftText}
              onChangeText={handleDraftTextChange}
              showImageCaptionInPreview={false}
              dismissKeyboardOnImagePick
              keepKeyboardOpenAfterSubmit
            />
          )}
        </Box>
        {isTabletSplitDetail && <Animated.View style={splitComposerInsetStyle} />}
      </VStack>

      {Platform.OS === 'web' && messageId && !isAdminConversation ? (
        <WebDropImageUpload
          isEnabled
          overlayLabel={
            data?.rootMessage?.Member?.UserName
              ? t('messages.dropImageToSendToUser', 'Drop image to send to {{userName}}', {
                  userName: data.rootMessage.Member.UserName,
                })
              : t('messages.dropImageToSend', 'Drop image to send')
          }
          title={t('messages.sendPhoto', 'Send Photo')}
          showCaption={false}
          isUploading={isImageUploading}
          onUpload={async (file) => {
            await handleImageUpload(file);
          }}
        />
      ) : null}
    </KeyboardAvoidingView>
  );
}
